/**
 * @notes: Endpoints, update the API version here, along with the DOMAIN that will be used
 * This will be used all across the application in several containers and actions.
 * This creates a central place for updates that will be globally affected.
 * Note that the window._env_?.NODE_ENV  variable is set when the application is started.
 * 1. production environment variable is set when a build is run using yarn build (or npm run build), after which
 *    the build can be run using yarn serve (or npm run serve)
 *
 * 2. staging environment variable is set using the yarn start:staging script (or npm run start:staging)
 *
 * 3. development environment variable is set using yarn start:dev script (or npm run start:dev).
 *    This will target using a mockApi
 *
 * 4. for Stroybook builds and deployments, we must set STORYBOOK_APP_ENV and STORYBOOK_APP_MAJOR_VERSION (see below)
 */

type Environment = "local" | "preprod" | "develop" | "staging" | "production";
type MajorVersion = "v3" | string;

interface ApiUrlOptions {
  environment?: Environment;
  majorVersion?: MajorVersion;
  includePath?: boolean;
  port?: number | null;
  customPath?: string;
}

const PROD_LATEST_VERSION = "v3";

// NOTE: Storybook removes all env vars not starting with STORYBOOK_
// see https://github.com/storybookjs/storybook/issues/12270 for ideas and workarounds
const APP_ENV = window._env_?.NODE_ENV || window._env_?.STORYBOOK_APP_ENV;
const APP_MAJOR_VERSION =
  window._env_?.MAJOR_VERSION || window._env_?.STORYBOOK_APP_MAJOR_VERSION;

const constructApiPath = (): string => {
  return "/web";
};

const constructApiPathWithPath = (): string => {
  return constructApiPath();
};

const constructApiPathWithoutPath = (): string => {
  return "";
};

export const constructBaseUrl = (
  environment: Environment,
  majorVersion: MajorVersion,
  includePath: boolean = true,
  port: number | null = null,
  customPath?: string,
): string => {
  // setup version for the url: add "v3." as prefix if we are in v3. If we are in prod env, write the prod version
  let versionForUrl = majorVersion === "v3" ? "v3." : "";

  // setup environment for the url. If we are in production, don't specify it.
  let envForUrl: string;

  let apiPath: string;
  if (customPath !== undefined) {
    // Use custom path, ensuring it starts with /
    apiPath = customPath?.startsWith("/") ? customPath : `/${customPath}`;
  } else {
    // Use default behavior
    apiPath = includePath
      ? constructApiPathWithPath()
      : constructApiPathWithoutPath();
  }

  const portSuffix = port ? `:${port}` : "";

  switch (environment) {
    case "local":
      const localPort = port || 8907;
      return `http://localhost:${localPort}${apiPath}`;
    case "preprod":
      return `https://v3.preprod.fieldproapp.com${portSuffix}${apiPath}`;
    case "develop":
      envForUrl = "develop";
      break;
    case "staging":
      envForUrl = "staging";
      break;
    case "production":
      envForUrl = "";
      versionForUrl = PROD_LATEST_VERSION;
      break;
  }

  return `https://${versionForUrl}${envForUrl}.fieldproapp.com${portSuffix}${apiPath}`;
};

export const API_BASE_URL = (options: ApiUrlOptions = {}): string => {
  const {
    environment = APP_ENV as Environment,
    majorVersion = APP_MAJOR_VERSION as MajorVersion,
    includePath = true,
    port = null,
    customPath,
  } = options;

  if (!majorVersion) {
    alert("Missing env variables MAJOR_VERSION");
    return "";
  }

  if (!environment) {
    throw new Error("Missing environment variable ENV (OR STORYBOOK_APP_ENV)");
  }

  return constructBaseUrl(
    environment,
    majorVersion,
    includePath,
    port,
    customPath,
  );
};
